import axios from "axios";
import { useAuthStore } from "../stores/authStore";

// Set global axios defaults for proper handling of multipart form data
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
axios.defaults.headers.common["Accept"] = "application/json";
// Don't set Content-Type globally as it interferes with FormData boundary setting

/**
 * Change the status of a document by ID, sending required resident fields
 * @param {number|string} id - Document ID
 * @param {string} status - New status
 * @param {object} doc - The full document object (must include resident fields)
 * @returns {Promise}
 */
export async function changeDocumentStatus(id, status, doc) {
    // Always send required resident fields for backend validation
    const payload = {
        status,
        resident_id: doc.resident_id ?? (doc.resident && doc.resident.id) ?? null,
        is_non_resident: doc.is_non_resident ?? false,
        resident_name: doc.resident_name ?? "",
        
    };
    return documentsAPI.update(id, payload);
}

/**
 * API Service Configuration
 *
 * Centralized API configuration with authentication handling
 */

// Create axios instance
const api = axios.create({
    baseURL:
        import.meta.env.VITE_API_URL ||
        (import.meta.env.MODE === "production"
            ? "/api/v1" // Use relative path in production
            : "http://localhost:8000/api/v1"), // Use localhost in development
    timeout: 30000,
    headers: {
        // Don't set Content-Type here as it prevents FormData from working correctly
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
    },
    // withCredentials: true, // Temporarily disabled to test CORS
}); // Request interceptor to add authentication token and enforce auth except for login/register
api.interceptors.request.use(
    (config) => {
        const token = useAuthStore.getState().token;
        const isAuthEndpoint =
            config.url?.endsWith("/login") || config.url?.endsWith("/register");

        // If not login/register, require token
        if (!isAuthEndpoint) {
            if (!token) {
                // Optionally, you can throw or reject here to block the request
                return Promise.reject({
                    message: "Authentication required",
                    config,
                });
            }
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor to handle authentication errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        // If we get a 401 error, logout the user
        if (error.response?.status === 401) {
            console.log("401 error detected, logging out user");
            useAuthStore.getState().logout();
            // Only redirect if we're not already on the login page
            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        }

        return Promise.reject(error);
    }
);
// Authentication API
export const authAPI = {
    login: (credentials) => api.post("/login", credentials),
    register: (userData) => api.post("/register", userData),
    logout: () => api.post("/auth/logout"),
    logoutAll: () => api.post("/auth/logout-all"),
    refresh: () => api.post("/auth/refresh"),
    me: () => api.get("/auth/me"),
    changePassword: (data) => api.post("/auth/change-password", data),
};

// Disputes API - Barangay Justice System
export const disputesAPI = {
    getAll: (params) => api.get("/disputes", { params }),
    getById: (id) => api.get(`/disputes/${id}`),
    create: (data) => {
        // Check if data is FormData (with files)
        const isFormData = data instanceof FormData;

        // For FormData, let Axios set the Content-Type header automatically with boundary
        return api.post(
            "/disputes",
            data,
            isFormData
                ? {
                    headers: {
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                }
                : {}
        );
    },
    update: (id, data) => {
        // Check if data is FormData (with files)
        const isFormData = data instanceof FormData;

        // For FormData, let Axios set the Content-Type header automatically with boundary
        return api.post(
            `/disputes/${id}`,
            data,
            isFormData
                ? {
                    headers: {
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                }
                : {}
        );
    },
    delete: (id) => api.delete(`/disputes/${id}`),
    addNotes: (id, data) => {
        // Check if data is FormData (with attachment files)
        const isFormData = data instanceof FormData;

        // Add debugging similar to updateNote
        if (isFormData) {
            const debugData = {};
            for (let [key, value] of data.entries()) {
                debugData[key] =
                    value instanceof File
                        ? `File: ${value.name} (${value.size} bytes)`
                        : value;
            }
            console.log(
                `Sending add notes request to /disputes/${id}/notes`,
                debugData
            );

            // Double check and log whether required fields are present
            console.log("FormData contains note_type:", data.has("note_type"));
            console.log("FormData contains content:", data.has("content"));
            console.log("FormData contains file:", data.has("attachment_file"));
        }

        // For FormData, let Axios set the Content-Type header automatically with boundary
        return api.post(
            `/disputes/${id}/notes`,
            data,
            isFormData
                ? {
                    headers: {
                        // Let axios set the Content-Type with boundary automatically
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                        // Remove Content-Type to let axios set it with proper boundary
                        "Content-Type": undefined,
                    },
                }
                : {}
        );
    },
    deleteNote: (disputeId, noteId) =>
        api.delete(`/disputes/${disputeId}/notes/${noteId}`),
    updateNote: (disputeId, noteId, data) => {
        const isFormData = data instanceof FormData;

        // Convert FormData to a plain object for debugging
        const debugData = {};
        if (isFormData) {
            for (let [key, value] of data.entries()) {
                debugData[key] =
                    value instanceof File
                        ? `File: ${value.name} (${value.size} bytes)`
                        : value;
            }
            console.log(
                `Sending update request to /disputes/${disputeId}/notes/${noteId}`,
                debugData
            );

            // Double check and log whether required fields are present
            console.log("FormData contains note_type:", data.has("note_type"));
            console.log("FormData contains content:", data.has("content"));
            console.log("FormData contains file:", data.has("attachment_file"));

            // Add these logs for debugging attachment deletion features
            console.log(
                "FormData contains remove_attachment:",
                data.has("remove_attachment")
            );
            console.log(
                "FormData contains keep_attachments:",
                data.has("keep_attachments")
            );
            console.log(
                "FormData contains attachment_indexes_to_keep:",
                data.has("attachment_indexes_to_keep")
            );
            console.log(
                "FormData contains remove_all_attachments:",
                data.has("remove_all_attachments")
            );

            // Log the actual value of attachment_indexes_to_keep if it exists
            if (data.has("attachment_indexes_to_keep")) {
                console.log(
                    "attachment_indexes_to_keep value:",
                    data.get("attachment_indexes_to_keep")
                );
            } // Log the actual values of key fields
            if (data.has("note_type")) {
                console.log("note_type value:", data.get("note_type"));
            }
            if (data.has("content")) {
                console.log("content length:", data.get("content").length);
            }
        } else {
            console.log(
                `Sending update request to /disputes/${disputeId}/notes/${noteId}`,
                data
            );
        }

        // For FormData, use POST with _method=PUT for Laravel's form method spoofing
        if (isFormData) {
            // Make sure _method field exists to ensure the server recognizes this as a PUT request
            if (!data.has("_method")) {
                data.append("_method", "PUT");
            }

            return api.post(`/disputes/${disputeId}/notes/${noteId}`, data, {
                headers: {
                    // Let axios set the Content-Type with boundary automatically
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    // Remove Content-Type to let axios set it with proper boundary
                    "Content-Type": undefined,
                },
            });
        } else {
            // For regular JSON, continue using PUT
            return api.put(`/disputes/${disputeId}/notes/${noteId}`, data);
        }
    },
    scheduleHearing: (id, data) => api.post(`/disputes/${id}/schedule`, data),
    updateHearing: (disputeId, hearingId, data) =>
        api.put(`/disputes/${disputeId}/hearing/${hearingId}`, data),
    updateAttendance: (disputeId, hearingId, data) => {
        console.log("Making attendance update request:", data);

        // Instead of PATCH which might be causing issues, use POST with a _method parameter
        // This is a common Laravel/Symfony convention for handling PATCH/PUT requests
        const url = `/disputes/${disputeId}/hearing/${hearingId}/attendance`;

        // Use a standard POST request with a _method parameter
        return api.post(url, {
            ...data,
            _method: "PATCH", // This tells Laravel to treat this as a PATCH request
        });
    },
    downloadSummons: (disputeId, hearingId) =>
        api.get(`/disputes/${disputeId}/hearing/${hearingId}/summons`, {
            responseType: "blob",
        }),
    // Get dispute details which includes hearings
    getDetails: (id) => api.get(`/disputes/${id}`),
    uploadEvidence: (id, formData) =>
        api.post(`/disputes/${id}/evidence`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    getEvidenceFiles: (id) => api.get(`/disputes/${id}/evidence`),
    downloadEvidence: (disputeId, evidenceId) =>
        api.get(`/disputes/${disputeId}/evidence/${evidenceId}`, {
            responseType: "blob",
        }),
    deleteEvidence: (disputeId, evidenceId) =>
        api.delete(`/disputes/${disputeId}/evidence/${evidenceId}`),
    downloadDocument: (id, documentType) =>
        api.get(`/disputes/${id}/document/${documentType}`, {
            responseType: "blob",
        }),
    downloadComplianceReport: (disputeId) =>
        api.get(`/disputes/${disputeId}/document/compliance-report`, {
            responseType: "blob",
        }),
    // Timeline data will be generated from dispute details in the frontend
    getStatistics: () => api.get("/disputes/statistics"),
    recordResolution: (id, data) => {
        // Check if data is FormData (with files)
        const isFormData = data instanceof FormData;

        // For FormData, let Axios set the Content-Type header automatically with boundary
        return api.post(
            `/disputes/${id}/resolve`,
            data,
            isFormData
                ? {
                    headers: {
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                }
                : {}
        );
    },
   addLupon: (id, data) => api.post(`/lupon_member/store/${id}`, data),
    addLuponDispute: (id, data) => api.post(`/disputes/${id}/legal`, data),

  getLupon: (id) => api.get(`/lupon_member/list/${id}`),
  getLuponRes: () => api.get("/lupon_member/resident-list"),

  updateLupon: (id, data) =>
    api.put(`/lupon_member/update/${id}`, data),

  deleteLupon: (id) =>
    api.put(`/lupon_member/delete/${id}`),


    


};

// Residents API
export const residentsAPI = {
    getAll: (params) => api.get("/residents", { params }),
    getById: (id) => api.get(`/residents/${id}`),
    create: (data) => api.post("/residents", data),
    update: (id, data) => api.put(`/residents/${id}`, data),
    delete: (id) => api.delete(`/residents/${id}`),
    uploadPhoto: (id, formData) =>
        api.post(`/residents/${id}/upload-photo`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    generateDigitalId: (id) => api.post(`/residents/${id}/generate-digital-id`),
    getHouseholdMembers: (id) => api.get(`/residents/${id}/household-members`),
        getPrint: (resident) => api.post(`/residents/${resident}/print-id`),

    getStatistics: () => api.get("/residents/statistics"),
    getCounts: () => api.get("/residents/counts"),

    // Search and lookup functions
    getUnassignedResidents: (params) =>
        api.get("/residents/unassigned", { params }),
    getHouseholdHeads: (params) =>
        api.get("/residents/household-heads", { params }),

    // Family member management
    deleteFamilyMember: (id) =>
        api.post(`/residents/${id}/remove-from-household`),
    detachFamilyMember: (id) =>
        api.post(`/residents/${id}/detach-from-household`),
    attachToHousehold: (id, householdId) =>
        api.post(`/residents/${id}/attach-to-household`, {
            household_id: householdId,
        }),
    assignToHousehold: (id, householdHeadId, additionalData = {}) =>
        api.post(`/residents/${id}/assign-household`, {
            household_head_id: householdHeadId,
            ...additionalData,
        }),
    getResidentAuditLogs: (params) =>
        api.get("/audit-logs/resident", { params }),
};

// Documents API
export const documentsAPI = {
    getAll: (params) => api.get("/documents", { params }),
    getById: (id) => api.get(`/documents/${id}`),
    create: (data) => api.post("/documents", data),
    update: (id, data) => api.put(`/documents/${id}`, data),
    delete: (id) => api.delete(`/documents/${id}`),
    track: (trackingNumber) =>
        api.post("/documents/track", { tracking_number: trackingNumber }),
    bulkUpdateStatus: (data) => api.post("/documents/bulk-update-status", data),
    getStatistics: () => api.get("/documents/statistics"),
};

// Document Types API
export const documentTypesAPI = {
    getAll: () => api.get("/document-types"), // fallback for legacy
    getAllV1: () => api.get("/document-types"),
    create: (data) => api.post("/document-types", data),
    update: (id, data) => api.put(`/document-types/${id}`, data),
    delete: (id) => api.delete(`/document-types/${id}`),
};

// Certificate Templates API
export const certificateTemplatesAPI = {
    getAll: () => api.get("/certificate-templates"),
    get: (id) => api.get(`/certificate-templates/${id}`),
    create: (data) => api.post("/certificate-templates", data),
    update: (id, data) => api.put(`/certificate-templates/${id}`, data),
    delete: (id) => api.delete(`/certificate-templates/${id}`),
};

// Complaints API
export const complaintsAPI = {
    getAll: (params) => api.get("/complaints", { params }),
    getById: (id) => api.get(`/complaints/${id}`),
    create: (data) => api.post("/complaints", data),
    update: (id, data) => api.put(`/complaints/${id}`, data),
    delete: (id) => api.delete(`/complaints/${id}`),
    assign: (id, data) => api.post(`/complaints/${id}/assign`, data),
    resolve: (id, data) => api.post(`/complaints/${id}/resolve`, data),
    getStatistics: () => api.get("/complaints/statistics"),
};

// Health Records API
export const healthRecordsAPI = {
    getAll: (params) => api.get("/health-records", { params }),
    getById: (id) => api.get(`/health-records/${id}`),
    create: (data) => api.post("/health-records", data),
    update: (id, data) => api.put(`/health-records/${id}`, data),
    delete: (id) => api.delete(`/health-records/${id}`),
    getByResident: (residentId) =>
        api.get(`/health-records/resident/${residentId}`),
    getStatistics: () => api.get("/health-records/statistics"),
};

// Emergency Records API
export const emergencyRecordsAPI = {
    getAll: (params) => api.get("/emergency-records", { params }),
    getById: (id) => api.get(`/emergency-records/${id}`),
    create: (data) => api.post("/emergency-records", data),
    update: (id, data) => api.put(`/emergency-records/${id}`, data),
    delete: (id) => api.delete(`/emergency-records/${id}`),
    getActive: () => api.get("/emergency-records/active"),
    getStatistics: () => api.get("/emergency-records/statistics"),
};

// Youth Records API
export const youthRecordsAPI = {
    getAll: (params) => api.get("/youth-records", { params }),
    getById: (id) => api.get(`/youth-records/${id}`),
    create: (data) => api.post("/youth-records", data),
    update: (id, data) => api.put(`/youth-records/${id}`, data),
    delete: (id) => api.delete(`/youth-records/${id}`),
    getStatistics: () => api.get("/youth-records/statistics"),
    getByOrganization: () => api.get("/youth-records/by-organization"),
    getSkillsSummary: () => api.get("/youth-records/skills-summary"),
    exportData: (params) => api.get("/youth-records/export", { params }),
};

// Curfew Violations API
export const curfewViolationsAPI = {
    getAll: (params) => api.get("/curfew-violations", { params }),
    getById: (id) => api.get(`/curfew-violations/${id}`),
    create: (data) => api.post("/curfew-violations", data),
    update: (id, data) => api.put(`/curfew-violations/${id}`, data),
    delete: (id) => api.delete(`/curfew-violations/${id}`),
    resolve: (id) => api.patch(`/curfew-violations/${id}/resolve`),
    getStatistics: () => api.get("/curfew-violations/statistics"),
    getByLocation: () => api.get("/curfew-violations/by-location"),
    getTrend: (params) => api.get("/curfew-violations/trend", { params }),
    getRepeatViolators: () => api.get("/curfew-violations/repeat-violators"),
    exportData: (params) => api.get("/curfew-violations/export", { params }),
};

// Payments API
export const paymentsAPI = {
    getAll: (params) => api.get("/payments", { params }),
    getById: (id) => api.get(`/payments/${id}`),
    create: (data) => api.post("/payments", data),
    update: (id, data) => api.put(`/payments/${id}`, data),
    delete: (id) => api.delete(`/payments/${id}`),
    process: (data) => api.post("/payments/process", data),
    refund: (id, data) => api.post(`/payments/${id}/refund`, data),
    getStatistics: () => api.get("/payments/statistics"),
};

// Analytics API
export const analyticsAPI = {
    getDashboard: () => api.get("/analytics/dashboard"),
    getReports: (params) => api.get("/analytics/reports", { params }),
    generateReport: (data) => api.post("/analytics/generate-report", data),
    getWidgets: () => api.get("/analytics/widgets"),
    createWidget: (data) => api.post("/analytics/widgets", data),
};

// Families API
export const familiesAPI = {
    getHouseholdFamilies: (householdId) =>
        api.get(`/families/household/${householdId}`),
    create: (data) => api.post("/families", data),
    getById: (id) => api.get(`/families/${id}`),
    update: (id, data) => api.put(`/families/${id}`, data),
    delete: (id) => api.delete(`/families/${id}`),
    addMember: (familyId, data) =>
        api.post(`/families/${familyId}/add-member`, data),
    removeMember: (residentId) => api.delete(`/families/member/${residentId}`),
    transferHeadship: (familyId, data) =>
        api.post(`/families/${familyId}/transfer-headship`, data),
};

// Add more API endpoints for other modules...
// This is a representative sample of the API structure

// Notifications API
export const notificationsAPI = {
    sendHearingNotifications: (data) => api.post("/notifications/hearing", data),
    getStatus: (notificationId) =>
        api.get(`/notifications/${notificationId}/status`),
    getDisputeNotifications: (disputeId) =>
        api.get(`/notifications/dispute/${disputeId}`),
};

// --------------------------VOTER API's----------------------------------------------------
export const votersAPI = {
    getDashboard: () => api.get("/voters/dashboard"),
    getAllResidents: () => api.get("/voters/residents"),
    getAll: (params) => api.get("/voters", { params }),
    getById: (id) => api.get(`/voters/${id}`),
    create: (data) => api.post("/voters", data),
    update: (id, data) => api.put(`/voters/${id}`, data),
    patch: (id, data) => api.patch(`/voters/${id}`, data),
    delete: (id) => api.delete(`/voters/${id}`),
    searchResident: (search) => api.get("/voters/search-resident", { params: { search } }),
    searchPrecinct: (q) => api.get("/voters/search-precinct", { params: { q } }),

    createPrecinct: (data) => api.post("/voters/precincts", data),
    getPrecincts: (params = { per_page: 10 }) =>
        api.get("/voters/precincts", { params }),
    getPrecinctById: (precinctId) => api.get(`/voters/precincts/${precinctId}`),
    getVotersByPrecinctId: (precinctId) =>
        api.get(`/voters/precincts/${precinctId}/voters`),
    updatePrecinct: (id, data) => api.put(`/voters/precincts/${id}`, data),
    deletePrecinct: (id) => api.delete(`/voters/precincts/${id}`),

    createVoter: (data) => api.post("/voters", data),
    updateVoter: (id, data) => api.patch(`/voters/${id}`, data),
    deleteVotersByPrecinctId: (id) => api.delete(`/voters/${id}`),

    createEducationMaterial: (data) =>
        api.post("/voters/education-materials", data),
    getEducationMaterials: (params = { per_page: 10 }) =>
        api.get("/voters/education-materials", { params }),
    getEducationMaterialById: (id) =>
        api.get(`/voters/education-materials/${id}`),
    updateEducationMaterial: (id, data) =>
        api.post(`/voters/education-materials/${id}`, data),
    deleteEducationMaterial: (id) =>
        api.delete(`/voters/education-materials/${id}`),

    createElectionTask: (data) => api.post("/voters/election-tasks", data),
    getElectionTasks: (params = { per_page: 10 }) =>
        api.get("/voters/election-tasks", { params }),
    getElectionTaskById: (id) => api.get(`/voters/election-tasks/${id}`),
    editElectionTask: (id, data) => api.put(`/voters/election-tasks/${id}`, data),
    deleteElectionTask: (id) => api.delete(`/voters/election-tasks/${id}`),
};

export const communityServicesAPI = {
    getDashboard: () => api.get("/community-services/dashboard"),
    getProgramReports: () => api.get("/community-services/program-reports"),
    getVolunteerReports: () => api.get("/community-services/volunteer-reports"),
    getServiceHoursReports: () => api.get("/community-services/service-hours"),
    getVolunteers: (params = {}) =>
        api.get("/community-services/programs/volunteers", { params }),
    getCategories: (params = {}) =>
        api.get("/community-services/programs/categories", { params }),
    createCategory: (data) =>
        api.post("/community-services/programs/categories", data),
    getCommunityServices: (params = {}) =>
        api.get("/community-services", { params }),
    getProgramById: (programId) => api.get(`/community-services/${programId}`),
    getAttedancesById: (programId) =>
        api.get(`/community-services/programs/${programId}/attendances`),
    searchOrganizer: (search) =>
        api.get("/community-services/volunteers/search-organizer", { params: { search },}),
    searchResidentVolunteer: (q) =>
        api.get("/community-services/volunteers/search-resident", {params: { q },}),
    getVolunteersByProgramId: (programId) =>
        api.get(`/community-services/programs/${programId}/volunteers`),
    createProgram: (data) => api.post("/community-services", data),

    updateProgram: (programId, data) =>
        api.put(`/community-services/${programId}`, data),
    updateProgramCategory: (categoryId, data) =>
        api.put(`/community-services/programs/categories/${categoryId}`, data),
    updateProgramAttendance: (programId, data) =>
        api.patch(`/community-services/programs/${programId}/attendances`, data),
    deleteProgram: (programId) => api.delete(`/community-services/${programId}`),
    deleteProgramCategory: (categoryId) =>
        api.delete(`/community-services/programs/categories/${categoryId}`),
    createManualVolunteerEntry: (data) =>
        api.post("/community-services/programs/volunteers/manual-entry", data),
    
    // Volunteer and Registration Management
    getVolunteersList: (params = {}) =>
        api.get("/community-services/volunteers", { params }),
    getVolunteerById: (volunteerId) =>
        api.get(`/community-services/volunteers/${volunteerId}`),   
    getVolunteerPrograms: () =>
        api.get("/community-services/volunteers/select-programs"),
    deleteVolunteer: (volunteerId) =>
        api.delete(`/community-services/volunteers/${volunteerId}`),
    createVolunteer: (data) =>
        api.post("/community-services/volunteers", data),
    updateVolunteer: (volunteerId, data) =>
        api.put(`/community-services/volunteers/${volunteerId}`, data),

};

export { api };
export default api;

export const institutionAPI = {
    getDashboard: () => api.get("/institutions/dashboard"),
    getPartnerships: (params = { per_page: 10, search: "" }) =>
        api.get("/institutions/partnerships", { params }),
    updatePartnership: (partnershipId, data) =>
        api.put(`/institutions/partnerships/${partnershipId}`, data),
    createPartnership: (data) => api.post("/institutions/partnerships", data),

    getInstitutions: (params = { per_page: 10, search: "", status: "", institution_type: "" }) =>
        api.get("/institutions", { params }),
    updateInstitutions: (institution, data) =>
        api.put(`/institutions/${institution}`, data),
    createInstitution: (data) => api.post("/institutions", data),
    deleteInstitution: (institution) =>
        api.delete(`/institutions/${institution}`),
    getInstitutionDetails: (institutionId) =>
        api.get(`/institutions/${institutionId}`),
    patchInstitution: (institutionId, data) =>
        api.patch(`/institutions/${institutionId}`, data),

    getInstitutionTypes: (params = { per_page: 10 }) =>
        api.get("/institutions/institution-types", { params }),
    createInstitutionType: (data) =>
        api.post("/institutions/institution-types", data),
    deleteInstitutionType: (institutionTypeId) =>
        api.delete(`/institutions/institution-types/${institutionTypeId}`),
    updateInstitutionType: (institutionTypeId, data) =>
        api.put(`/institutions/institution-types/${institutionTypeId}`, data),
    getSelectInstitutionTypes: () =>
        api.get("/institutions/select-institution-types"),
    getListInstitutionTypes: () => api.get("/institutions/institution-types"),
    
    getCollaborationTypes: (params = { per_page: 10, search: "" }) =>
        api.get("/institutions/collaboration-types", { params }),
    createCollaborationType: (data) =>
        api.post("/institutions/collaboration-types", data),
    updateCollaborationType: (collaborationTypeId, data) =>
        api.put(`/institutions/collaboration-types/${collaborationTypeId}`, data),
    deleteCollaborationType: (collaborationTypeId) =>
        api.delete(`/institutions/collaboration-types/${collaborationTypeId}`),

    getPartnerTypes: (params = { per_page: 10, search: "" }) =>
        api.get("/institutions/partner-types", { params }),
    createPartnerType: (data) =>
        api.post("/institutions/partner-types", data),
    updatePartnerType: (partnerTypeId, data) =>
        api.put(`/institutions/partner-types/${partnerTypeId}`, data),
    deletePartnerType: (partnerTypeId) =>
        api.delete(`/institutions/partner-types/${partnerTypeId}`),

};

export const budgetAllocationAPI = {
    //COA ACCOUNT GROUP API ENDPOINTS
    getAccountGroups: (params = {}) =>
        api.get("budget-allocations/coa-account-groups", { params }),
    getAccountGroupById: (accountGroupId) =>
        api.get(`budget-allocations/coa-account-groups/${accountGroupId}`),
    createAccountGroup: (data) =>
        api.post("budget-allocations/coa-account-groups", data),
    updateAccountGroup: (accountGroupId, data) =>
        api.patch(`budget-allocations/coa-account-groups/${accountGroupId}`, data),
    deleteAccountGroup: (accountGroupId) =>
        api.delete(`budget-allocations/coa-account-groups/${accountGroupId}`),

    //FUND SOURCE API ENDPOINTS
    getFundSources: (params = {}) =>
        api.get("budget-allocations/fund-sources", { params }),
    getFundSourceById: (fundSourceId) =>
        api.get(`budget-allocations/fund-sources/${fundSourceId}`),
    createFundSource: (data) => api.post("budget-allocations/fund-sources", data),
    updateFundSource: (fundSourceId, data) =>
        api.patch(`budget-allocations/fund-sources/${fundSourceId}`, data),
    deleteFundSource: (fundSourceId) =>
        api.delete(`budget-allocations/fund-sources/${fundSourceId}`),
    //BUDGET ITEM GROUP API ENDPOINTS
    getBudgetItemGroups: (params = {}) =>
        api.get(`budget-allocations/budget-item-groups`, { params }),
    getBudgetItemGroupById: (budgetItemGroupId) =>
        api.get(`budget-allocations/budget-item-groups/${budgetItemGroupId}`),
    createBudgetItemGroup: (data) =>
        api.post("budget-allocations/budget-item-groups", data),
    updateBudgetItemGroup: (budgetItemGroupId, data) =>
        api.patch(
            `budget-allocations/budget-item-groups/${budgetItemGroupId}`,
            data
        ),
    deleteBudgetItemGroup: (budgetItemGroupId) =>
        api.delete(`budget-allocations/budget-item-groups/${budgetItemGroupId}`),
    //BUDGET ITEM API ENDPOINTS
    getBudgetItems: (params = {}) =>
        api.get("budget-allocations/budget-items", { params }),
    getBudgetItemById: (budgetItemId) =>
        api.get(`budget-allocations/budget-items/${budgetItemId}`),
    createBudgetItem: (data) => api.post("budget-allocations/budget-items", data),
    updateBudgetItem: (budgetItemId, data) =>
        api.patch(`budget-allocations/budget-items/${budgetItemId}`, data),
    deleteBudgetItem: (budgetItemId) =>
        api.delete(`budget-allocations/budget-items/${budgetItemId}`),
};

export const legislationsAPI = {
    getDashboard: () => api.get("/legislations/dashboard"),
    getCommittees: (params = { per_page: "10", search: "" }) =>
        api.get("/legislations/committees", { params }),
    createCommittee: (data) => api.post("/legislations/committees", data),
    getChairmanships: () =>
        api.get("/legislations/committees/select-chairmanships"),
    updateCommittee: (committeeId, data) =>
        api.patch(`/legislations/committees/${committeeId}`, data),

    //LEGISLATION API ENDPOINTS
    createLegislativeDocument: (data) => api.post("/legislations", data),
    getListLegislativeDocuments: (params = {}) =>
        api.get("/legislations", { params }),
    getCommitteesSelect: () => api.get("/legislations/select-committees"),
    getSponsorsSelect: () => api.get("/legislations/select-sponsors"),
    getChairmanshipSelect: () =>
        api.get("/legislations/committees/select-chairmanships"),
    getLegislativeDocuments: (params = { per_page: "10", search: "" }) =>
        api.get("/legislations", { params }),
    getLegislativeDocumentById: (documentId, params = {}) =>
        api.get(`/legislations/${documentId}`, { params }),
    updateLegislativeDocument: (documentId, data) =>
        api.post(`/legislations/${documentId}`, data),
    archiveLegislativeDocument: (documentId, remarks = "") =>
        api.post(`/legislations/${documentId}/archive`, { remarks }),
    getLegislativeDocumentVersions: (documentId) =>
        api.get(`/legislations/${documentId}/versions`),
    getLegislativeDocumentVersionById: (documentId, versionId) =>
        api.get(`/legislations/${documentId}/versions/${versionId}`),
    // Note: This endpoint uses PATCH method and different URL pattern
    // Use updateLegislativeDocumentVersion (below) for the /update endpoint
    updateLegislativeDocumentVersionV1: (documentId, versionId, data) =>
        api.patch(`/legislations/${documentId}/versions/${versionId}`, data),
    deleteLegislativeDocumentVersion: (documentId, versionId) =>
        api.delete(`/legislations/${documentId}/versions/${versionId}`),

    //CAMILLE API ENDPOINTS
    getTabView: (params = {}) =>
        api.get("/legislations/tab-view", { params }),
    updateConsultationRecord: (consultationId, data) =>
        api.post(`/legislations/consultation-records/${consultationId}`, data),
    getConsultationRecord: (consultationId) =>
        api.get(`/legislations/consultation-records/${consultationId}`),
    getLegislationById: (legislativeId, include = "") =>
        api.get(`/legislations/${legislativeId}`, { params: { include } }),
    restoreArchivedLegislation: (legislativeId) =>
        api.patch(`/legislations/${legislativeId}/restore-archived`),
    uploadConsultationRecord: (legislativeId, formData) => {
        return api.post(
            `/legislations/${legislativeId}/consultation-records/upload-new`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
    },
    archiveCommittee: (committee) =>
        api.delete(`/legislations/committees/${committee}/archive`),
    restoreCommittee: (committee) =>
        api.patch(`/legislations/committees/${committee}/restore-archived`),
    /**
     * Upload a new legislation document version
     * @param {number|string} legislativeId - The legislation document ID
     * @param {FormData} formData - FormData containing support_documents[], summary, and is_final
     * @returns {Promise} Axios response
     */
    uploadNewLegislationDocumentVersion: (legislativeId, formData) => {
        const isFormData = formData instanceof FormData;

        return api.post(
            `/legislations/${legislativeId}/document-versions/upload-new`,
            formData,
            isFormData
                ? {
                    headers: {
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                        // Let axios set Content-Type with boundary automatically
                        "Content-Type": undefined,
                    },
                }
                : {}
        );
    },

    /**
     * Get a specific legislation document version by ID
     * @param {number|string} versionId - The version ID
     * @returns {Promise} Axios response
     */
    getLegislativeDocumentVersionByDocumentId: (versionId) =>
        api.get(`/legislations/document-versions/${versionId}`),

    /**
     * Update an existing legislation document version
     * @param {number|string} legislativeId - The legislation document ID
     * @param {number|string} versionId - The version ID to update
     * @param {FormData|Object} data - FormData or object containing change_summary, legislative_file (optional), is_final, remove_legislative_file
     * @returns {Promise} Axios response
     */
    updateLegislativeDocumentVersion: (legislativeId, versionId, data) => {
        const isFormData = data instanceof FormData;

        // For FormData, let Axios set the Content-Type header automatically with boundary
        return api.post(
            `/legislations/${legislativeId}/document-versions/${versionId}/update`,
            data,
            isFormData
                ? {
                    headers: {
                        Accept: "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                        // Let axios set Content-Type with boundary automatically
                        "Content-Type": undefined,
                    },
                }
                : {}
        );
    },
};

export const antiDrugAPI = {
    //Dashboard
     getAntiDrugDashboard: (params = {}) =>
        api.get("anti-drug-records/dashboard", { params }),

    //Surrenderee's Table
    getSurrenderees: (params = { per_page: "10", search: "" }) =>
        api.get("anti-drug-records/surrenderees", { params }),
    getSelectableRehabilitationCenters: () =>
        api.get("anti-drug-records/surrenderees/select-rehabilitation-center"),
    // Tab View
    getSurrendereeById: (surrendereeId) =>
        api.get(`anti-drug-records/surrenderees/${surrendereeId}`),
    updateSurrenderee: (surrendereeId, payload) =>
        api.patch(`anti-drug-records/surrenderees/${surrendereeId}`, payload),
    getSurrendereeProgramHistories: (surrendereeId, params = { per_page: "10", search: "" }) =>
        api.get(`anti-drug-records/surrenderees/${surrendereeId}/program-histories`, { params }),
    getSurrendereeMonitoringLogs: (surrendereeId, params = { per_page: "10" }) =>
        api.get(`anti-drug-records/surrenderees/${surrendereeId}/monitoring-logs`, { params }),
    getSurrendereeDocuments: (surrendereeId, params = { per_page: "10", search: "" }) =>
        api.get(`anti-drug-records/surrenderees/${surrendereeId}/document-records`, { params }),
    deleteSurrendereeDocument: (documentId) =>
        api.delete(`anti-drug-records/surrenderees/${documentId}/delete-document-records`),
    downloadSurrendereeDocument: (documentId) =>
        api.post(
            `anti-drug-records/surrenderees/${documentId}/download-document-records`,
            {},
            {
                responseType: "blob",
            }
        ),
     uploadSurrendereeDocument: (surrendereeId, formData) =>
        api.post(
            `anti-drug-records/surrenderees/${surrendereeId}/upload-document-records`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        ),

    // Rehabilitation Programs
    createRehabProgram: (payload) =>
        api.post("anti-drug-records/rehab-programs", payload),
    getRehabPrograms: (params = {}) =>
        api.get("anti-drug-records/rehab-programs", { params }),
    getRehabProgramSurrenderees: (rehabProgramId, params = {}) =>
        api.get(`anti-drug-records/rehab-programs/${rehabProgramId}/surrenderees`, { params }),
    getRehabProgramById: (rehabProgramId) =>
        api.get(`anti-drug-records/rehab-programs/${rehabProgramId}`),
    getSelectableSurrenderees: () =>
        api.get("anti-drug-records/rehab-programs/select-surrenderees"),
    updateRehabProgram: (rehabProgramId, payload) =>
        api.patch(`anti-drug-records/rehab-programs/${rehabProgramId}`, payload),
    // Create New Participants
    searchSurrendereeResident: (search) =>
        api.get("anti-drug-records/surrenderees/search-resident", { params: { search } }),
    getSelectableRehabPrograms: () =>
        api.get("anti-drug-records/surrenderees/select-rehab-programs"),
    createSurrenderee: (payload) =>
        api.post("anti-drug-records/surrenderees", payload),


    //Community Programs
    createCommunityProgram: (formData) =>
        api.post("anti-drug-records/community-programs", formData),
    getCommunityPrograms: (params = {}) =>
        api.get("anti-drug-records/community-programs", { params }),
    updateCommunityProgram: (communityProgramId, payload) =>
        api.post(`anti-drug-records/community-programs/${communityProgramId}`, payload),
    deleteCommunityProgramAttachment: (communityProgramId, filePath) =>
        api.delete(
            `anti-drug-records/community-programs/${communityProgramId}/delete-attachment`,
            {
                params: { file_path: filePath },
            }
        ),
    downloadCommunityProgramAttachment: (communityProgramId, filePath) =>
        api.post(
            `anti-drug-records/community-programs/${communityProgramId}/attachments/download`,
            { file_path: filePath },
            {
                responseType: "blob",
            }
        ),

    //Coordination Logs
    getCoordinationLogs: (params = {}) =>
        api.get("anti-drug-records/coordination-logs", { params }),
    updateCoordinationLog: (coordinationLogId, payload) =>
        api.post(`anti-drug-records/coordination-logs/${coordinationLogId}`, payload),
    createCoordinationLog: (formData) =>
        api.post("anti-drug-records/coordination-logs", formData),
    deleteCoordinationLogAttachment: (coordinationLogId, filePath) =>
        api.delete(
            `anti-drug-records/coordination-logs/${coordinationLogId}/delete-attachment`,
            {
                params: { file_path: filePath },
            }
        ),
    downloadCoordinationLogAttachment: (coordinationLogId, filePath) =>
        api.post(
            `anti-drug-records/coordination-logs/${coordinationLogId}/attachments/download`,
            { file_path: filePath },
            {
                responseType: "blob",
            }
        ),
};

//AYUDA APIs

export const ayudaApi = {
    getAyuda: (params) => api.get("/ayuda-program", { params }),
    getByAyudaId: (ayudaProgram) => api.get(`/ayuda-program/${ayudaProgram}`),
        getByAyudaResidents: (ayudaProgram, params) => api.get(`/ayuda-program/${ayudaProgram}/residents`, { params }),
        getAyudaFilterResidents: (params) => api.get("/ayuda-program/residents", { params }),
        getAyudaTransactionByResidentsId: (residentsId, params) => api.get(`/ayuda-program/transactions/${residentsId}`, { params }),
    createAyuda: (data) => api.post("/ayuda-program", data),
    updateAyuda: (ayudaProgramId, payload) => api.put(`/ayuda-program/${ayudaProgramId}`, payload),
        removeAyuda: (ayudaProgram) => api.delete(`/ayuda-program/${ayudaProgram}`),
    getAyudaStats: () => api.get("/ayuda-program/statistics"),
disburseAyuda: (ayudaProgram, payload) =>
  api.post(`/ayuda-program/${ayudaProgram}/disburse`, payload),

    getAyudaLogs: (params) => api.get("/audit-logs/ayuda", { params }),

    getResidentWalletTransactions: (residentId, params) =>
        api.get(`/ayuda-program/wallets/${residentId}`, { params }),


}   

// SELF-SERVICE API
export const selfServiceAPI = {

    getProfile: () => api.get("/self-services/me/profile"),
}